import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import {
  iso, nbJoursDuMois, jourSemaine, moisDeSaison, nomMois, formatCourt, aujourdhui, parseIso,
} from '../../utils/planning'
import './CalendrierOuverture.css'

export default function CalendrierOuverture() {
  const [saisons, setSaisons] = useState([])
  const [saisonId, setSaisonId] = useState(null)
  const [periode, setPeriode] = useState({ debut: '', fin: '', accueil: true, supervision: true, hydro: false, action: 'fermer', libelle: '' })
  const [appliquant, setAppliquant] = useState(false)
  const [cle, setCle] = useState('')          // 'AAAA-MM'
  const [jours, setJours] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)

  const saison = saisons.find(x => x.id === saisonId) || null

  useEffect(() => {
    supabase.from('planning_saisons').select('*').order('date_debut', { ascending: false })
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) {
          setLoading(false)
          if (error) setMsg({ type: 'error', text: 'Impossible de charger la saison.' })
          return
        }
        setSaisons(data)
        const auj = aujourdhui()
        const courante = data.find(x => x.date_debut <= auj && auj <= x.date_fin) || data[0]
        setSaisonId(courante.id)
      })
  }, [])

  useEffect(() => {
    if (!saison) return
    const mois = moisDeSaison(saison)
    const auj = parseIso(aujourdhui())
    const courant = mois.find(m => m.annee === auj.annee && m.mois === auj.mois) || mois[0]
    setCle(`${courant.annee}-${String(courant.mois).padStart(2, '0')}`)
    setPeriode(p => ({ ...p, debut: saison.date_debut, fin: saison.date_debut }))
  }, [saison])

  const charger = useCallback(async () => {
    if (!cle) return
    setLoading(true)
    const [a, m] = cle.split('-').map(Number)
    const { data, error } = await supabase.from('jours_planning').select('*')
      .gte('date', iso(a, m, 1)).lte('date', iso(a, m, nbJoursDuMois(a, m))).order('date')
    if (error) setMsg({ type: 'error', text: 'Erreur lors du chargement du calendrier.' })
    setJours(data || [])
    setLoading(false)
  }, [cle])

  useEffect(() => { charger() }, [charger])

  async function maj(date, champs) {
    const avant = jours
    setJours(js => js.map(j => (j.date === date ? { ...j, ...champs } : j)))
    setMsg(null)
    const { error } = await supabase.from('jours_planning').update(champs).eq('date', date)
    if (error) {
      setJours(avant)
      setMsg({ type: 'error', text: "La modification n'a pas pu être enregistrée." })
    }
  }

  async function appliquerPeriode() {
    setMsg(null)
    const { debut, fin, accueil, supervision, hydro, action, libelle } = periode
    if (!debut || !fin || fin < debut) { setMsg({ type: 'error', text: 'Période invalide.' }); return }
    if (!saison || debut < saison.date_debut || fin > saison.date_fin) {
      setMsg({ type: 'error', text: 'La période doit être comprise dans la saison.' })
      return
    }
    if (!accueil && !supervision && !hydro) { setMsg({ type: 'error', text: 'Choisis au moins un poste.' }); return }
    const ouvrir = action === 'ouvrir'
    const champs = {}
    if (accueil) champs.accueil_ouvert = ouvrir
    if (supervision) champs.supervision_ouverte = ouvrir
    if (hydro) champs.hydro_ouvert = ouvrir
    if (accueil && !ouvrir) champs.accueil_majore = false
    if (!ouvrir && libelle.trim()) champs.libelle = libelle.trim()
    setAppliquant(true)
    const { error } = await supabase.from('jours_planning').update(champs).gte('date', debut).lte('date', fin)
    if (!error && accueil && ouvrir) {
      // les feries ouverts a l'accueil passent au tarif majore par defaut
      const r = await supabase.from('jours_planning').update({ accueil_majore: true })
        .gte('date', debut).lte('date', fin).eq('ferie', true)
      if (r.error) { setAppliquant(false); setMsg({ type: 'error', text: 'Période appliquée, mais le tarif majoré des fériés n\'a pas pu être mis à jour.' }); charger(); return }
    }
    setAppliquant(false)
    if (error) { setMsg({ type: 'error', text: "La période n'a pas pu être enregistrée." }); return }
    setMsg({ type: 'success', text: `Période du ${formatCourt(debut)} au ${formatCourt(fin)} mise à jour.` })
    charger()
  }

  if (!saison && !loading) {
    return (
      <div>
        <div className="page-header"><div><h2>Calendrier d'ouverture</h2></div></div>
        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
        <div className="card" style={{ textAlign: 'center', color: 'var(--stone)', padding: 32 }}>
          Aucune saison de planning. Exécute d'abord la migration supabase-planning.sql.
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Calendrier d'ouverture</h2>
          <div className="page-header-sub">
            Fermetures de l'accueil, de la supervision et de l'hydro. Visible par les superviseurs dans leurs disponibilités.
          </div>
        </div>
        {saisons.length > 1 && (
          <select value={saisonId || ''} onChange={e => setSaisonId(e.target.value)}>
            {saisons.map(x => <option key={x.id} value={x.id}>{x.libelle}</option>)}
          </select>
        )}
        {saison && (
          <select value={cle} onChange={e => setCle(e.target.value)}>
            {moisDeSaison(saison).map(({ annee, mois }) => {
              const v = `${annee}-${String(mois).padStart(2, '0')}`
              return <option key={v} value={v}>{nomMois(mois)} {annee}</option>
            })}
          </select>
        )}
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="alert alert-info">
        Le tarif d'accueil majoré s'applique aux jours fériés et aux samedis sans la coordinatrice. Il est coché par défaut
        sur les fériés où l'accueil est ouvert. Les samedis d'absence sont à cocher ici.
      </div>

      {saison && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12 }}>Modifier une période</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0 16px' }}>
            <div className="form-group">
              <label>Du</label>
              <input type="date" value={periode.debut} min={saison.date_debut} max={saison.date_fin}
                onChange={e => setPeriode(p => ({ ...p, debut: e.target.value, fin: p.fin < e.target.value ? e.target.value : p.fin }))} />
            </div>
            <div className="form-group">
              <label>Au</label>
              <input type="date" value={periode.fin} min={saison.date_debut} max={saison.date_fin}
                onChange={e => setPeriode(p => ({ ...p, fin: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Action</label>
              <select value={periode.action} onChange={e => setPeriode(p => ({ ...p, action: e.target.value }))}>
                <option value="fermer">Fermer</option>
                <option value="ouvrir">Ouvrir</option>
              </select>
            </div>
            <div className="form-group">
              <label>Libellé (à la fermeture)</label>
              <input value={periode.libelle} placeholder="ex. SDE & ACC FERMÉ"
                onChange={e => setPeriode(p => ({ ...p, libelle: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
            {[['accueil', 'Accueil'], ['supervision', 'Supervision'], ['hydro', 'Hydro']].map(([k, l]) => (
              <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={periode[k]} onChange={e => setPeriode(p => ({ ...p, [k]: e.target.checked }))} />
                {l}
              </label>
            ))}
            <button className="btn btn-bordeaux btn-sm" onClick={appliquerPeriode} disabled={appliquant}>
              {appliquant ? 'Application...' : 'Appliquer'}
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Jour</th>
                <th className="cal-centre">Accueil ouvert</th>
                <th className="cal-centre">Supervision ouverte</th>
                <th className="cal-centre">Hydro ouvert</th>
                <th className="cal-centre">Cours SHP</th>
                <th className="cal-centre">Accueil tarif majoré</th>
                <th>Libellé</th>
              </tr>
            </thead>
            <tbody>
              {jours.map(j => {
                const sem = jourSemaine(j.date)
                const toutFerme = !j.accueil_ouvert && !j.supervision_ouverte && !j.hydro_ouvert
                let classe = ''
                if (toutFerme) classe = 'cal-ferme'
                else if (sem >= 5) classe = 'cal-weekend'
                return (
                  <tr key={j.date} className={classe}>
                    <td>
                      <strong>{formatCourt(j.date)}</strong>
                      {j.ferie && <span className="badge badge-warning" style={{ marginLeft: 8 }}>Férié</span>}
                    </td>
                    <td className="cal-centre">
                      <input type="checkbox" className="cal-check" checked={j.accueil_ouvert}
                        onChange={e => maj(j.date, e.target.checked ? (j.ferie ? { accueil_ouvert: true, accueil_majore: true } : { accueil_ouvert: true }) : { accueil_ouvert: false, accueil_majore: false })} />
                    </td>
                    <td className="cal-centre">
                      <input type="checkbox" className="cal-check" checked={j.supervision_ouverte}
                        onChange={e => maj(j.date, { supervision_ouverte: e.target.checked })} />
                    </td>
                    <td className="cal-centre">
                      <input type="checkbox" className="cal-check" checked={j.hydro_ouvert}
                        onChange={e => maj(j.date, { hydro_ouvert: e.target.checked })} />
                    </td>
                    <td className="cal-centre">
                      <input type="checkbox" className="cal-check" checked={j.cours_shp}
                        onChange={e => maj(j.date, { cours_shp: e.target.checked })} />
                    </td>
                    <td className="cal-centre">
                      <input type="checkbox" className="cal-check" checked={j.accueil_majore} disabled={!j.accueil_ouvert}
                        onChange={e => maj(j.date, { accueil_majore: e.target.checked })} />
                    </td>
                    <td>
                      <input
                        className="cal-libelle"
                        key={`${j.date}|${j.libelle || ''}`}
                        defaultValue={j.libelle || ''}
                        onBlur={e => {
                          const v = e.target.value.trim()
                          if (v !== (j.libelle || '')) maj(j.date, { libelle: v || null })
                        }}
                      />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
